import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  occurrenceWindow,
  previewOccurrenceDates,
  type DaySlot,
  type RecurrenceRule,
} from '@shared/calculations/recurrence.ts';
import { DAYS_OF_WEEK, SERIES_FREQUENCIES, type SeriesFrequency } from '@shared/constants/index.ts';
import { Button, Checkbox, Field, Select, TextInput } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';
import { MoneyInput } from '../money-input.tsx';
import { AssigneePicker } from './assignee-picker.tsx';
import { formatLocalTime, formatShortDate } from '../../i18n/datetime.ts';

/**
 * The recurrence-rule form, shared by "new schedule" and the series editor.
 *
 * Controlled by the parent through a flat state object. `monthlyMode` is a
 * UI-only field — the API expresses the mode by which of dayOfMonth / nthWeek
 * is set, and `ruleToApiFields` translates. Several weekdays, an interval, and
 * a per-day time or crew travel as `daySlots`.
 */
export interface DayOverride {
  /** Blank uses the schedule's start time. */
  startTimeLocal: string;
  /** Null uses the schedule's crew. An empty list is that day, unassigned. */
  assignedUserIds: string[] | null;
  /**
   * Set when this weekday runs for a different length than the schedule.
   * The form does not edit it; a save has to carry it or the next notes
   * edit regenerates those visits at the schedule's length.
   */
  durationMinutes?: number;
  /**
   * This weekday's own price. Null or absent uses the schedule's price.
   * Belongs to the weekday: on a monthly split (1st and 3rd Tuesday) it is
   * edited on the first of that weekday's rows and applies to both.
   */
  priceCents?: number | null;
  /** A checklist template for this weekday. Null or absent uses the schedule's checklist. */
  checklistTemplateId?: string | null;
}

export interface RuleFormState {
  frequency: SeriesFrequency;
  /** First selected weekday. Kept so a pick and older readers still have one day. */
  dayOfWeek: number;
  /** Weekdays this schedule visits. One entry is an ordinary weekly day. */
  weekdays: number[];
  /** Every N weeks. 1 is weekly; 2 is every other week. */
  intervalWeeks: number;
  monthlyMode: 'day_of_month' | 'nth_weekday';
  dayOfMonth: number;
  /** First selected week-of-month. */
  nthWeek: number;
  /** Which weeks of the month, on a monthly weekday rule. 5 means last. */
  nthWeeks: number[];
  /**
   * Per-day differences. A weekly day is keyed by its weekday (`"1"`). A
   * monthly slot that shares its weekday with another is keyed by weekday and
   * weeks (`"2:1"`, `"2:3"`), so the first Tuesday and the third Tuesday keep
   * their own time and crew.
   */
  dayOverrides: Record<string, DayOverride>;
  /**
   * Monthly slots that share a weekday. Kept until the office changes which
   * days or which weeks are selected; a save then emits these slots rather
   * than one slot per weekday with the weeks unioned onto it.
   */
  preservedSlots?: DaySlot[] | null;
  startTimeLocal: string;
  durationMinutes: number;
  startsOn: string;
  endsOn: string;
}

function sortedUnique(days: readonly number[]): number[] {
  return [...new Set(days.filter((day) => day >= 0 && day <= 6))].sort((left, right) => left - right);
}

/** Key for one slot's time and crew. Weekly days are just the weekday. */
export function slotOverrideKey(dayOfWeek: number, nthWeeks?: readonly number[] | null): string {
  if (nthWeeks && nthWeeks.length > 0) {
    return `${dayOfWeek}:${[...nthWeeks].sort((left, right) => left - right).join(',')}`;
  }
  return String(dayOfWeek);
}

/**
 * Where this slot's time and crew live on the form.
 *
 * A weekday that appears twice (first Tuesday and third Tuesday) needs the
 * week in the key. A weekday that appears once keeps the plain weekday key,
 * so clearing the split pattern — which looks overrides up by weekday — still
 * finds that day's time and crew.
 */
function overrideKeyFor(slots: readonly DaySlot[], slot: DaySlot): string {
  const repeats = slots.filter((other) => other.dayOfWeek === slot.dayOfWeek).length > 1;
  return repeats ? slotOverrideKey(slot.dayOfWeek, slot.nthWeeks) : String(slot.dayOfWeek);
}

/**
 * The stored monthly slots, when the form still describes them.
 *
 * Changing the weekday checkboxes or the week-of-month checkboxes drops the
 * split: the office is describing a new pattern, and one slot per weekday is
 * what those checkboxes mean. Leaving them alone has to round-trip the slots
 * the series was saved with, including two Tuesdays that start at different
 * hours.
 */
function matchingPreservedSlots(rule: RuleFormState): DaySlot[] | null {
  const slots = rule.preservedSlots;
  if (!slots || slots.length < 2) return null;
  if (rule.frequency !== 'monthly' || rule.monthlyMode !== 'nth_weekday') return null;
  const slotDays = sortedUnique(slots.map((slot) => slot.dayOfWeek));
  const slotNths = sortedUnique(slots.flatMap((slot) => slot.nthWeeks ?? []));
  const days = sortedUnique(rule.weekdays.length > 0 ? rule.weekdays : [rule.dayOfWeek]);
  const nths = sortedUnique(rule.nthWeeks.length > 0 ? rule.nthWeeks : [rule.nthWeek]);
  if (slotDays.join(',') !== days.join(',') || slotNths.join(',') !== nths.join(',')) return null;
  const seen = new Set<number>();
  const repeated = slots.some((slot) => {
    const again = seen.has(slot.dayOfWeek);
    seen.add(slot.dayOfWeek);
    return again;
  });
  return repeated ? slots : null;
}

/**
 * Drop a monthly split (two Tuesdays on different weeks) and carry each
 * weekday's price and checklist across to the plain weekday row the form
 * reads once the split is gone. Those live on the split's first row for the
 * weekday (`2:1`); left there, they would silently vanish from the form and
 * the next save would clear them. A value already on the plain row wins.
 */
export function withoutSplit(rule: RuleFormState): RuleFormState {
  const slots = rule.preservedSlots;
  if (!slots || slots.length === 0) return { ...rule, preservedSlots: null };
  const dayOverrides = { ...rule.dayOverrides };
  for (const day of new Set(slots.map((slot) => slot.dayOfWeek))) {
    const key = weekdayKeyFor(slots, day);
    const split = rule.dayOverrides[key];
    const plain = dayOverrides[String(day)];
    if (key === String(day) || !split) continue;
    if (split.priceCents == null && !split.checklistTemplateId) continue;
    if (plain && (plain.priceCents != null || plain.checklistTemplateId)) continue;
    dayOverrides[String(day)] = {
      ...(plain ?? { startTimeLocal: '', assignedUserIds: null }),
      ...(split.priceCents != null ? { priceCents: split.priceCents } : {}),
      ...(split.checklistTemplateId ? { checklistTemplateId: split.checklistTemplateId } : {}),
    };
  }
  return { ...rule, dayOverrides, preservedSlots: null };
}

/** Apply a frequency change, including the interval a biweekly quote implies. */
export function withFrequency(rule: RuleFormState, frequency: SeriesFrequency): RuleFormState {
  let intervalWeeks = rule.intervalWeeks;
  if (frequency === 'biweekly' && intervalWeeks === 1) intervalWeeks = 2;
  if (frequency === 'weekly' && rule.frequency === 'biweekly' && intervalWeeks === 2) intervalWeeks = 1;
  const next = { ...rule, frequency, intervalWeeks };
  return frequency === 'monthly' ? { ...next, preservedSlots: rule.preservedSlots ?? null } : withoutSplit(next);
}

function slotFromOverride(
  dayOfWeek: number,
  nthWeeks: number[] | null,
  override: DayOverride | undefined,
  /** Where this weekday's price and checklist live: the first row for the weekday. */
  weekday: DayOverride | undefined = override,
): DaySlot {
  return {
    dayOfWeek,
    ...(nthWeeks ? { nthWeeks } : {}),
    ...(override?.startTimeLocal ? { startTimeLocal: override.startTimeLocal } : {}),
    ...(override?.durationMinutes ? { durationMinutes: override.durationMinutes } : {}),
    ...(override?.assignedUserIds != null ? { assignedUserIds: override.assignedUserIds } : {}),
    ...(weekday?.priceCents != null ? { priceCents: weekday.priceCents } : {}),
    ...(weekday?.checklistTemplateId ? { checklistTemplateId: weekday.checklistTemplateId } : {}),
  };
}

/** The row that holds a weekday's price and checklist: the first slot on that weekday. */
function weekdayKeyFor(slots: readonly DaySlot[], dayOfWeek: number): string {
  const first = slots.find((slot) => slot.dayOfWeek === dayOfWeek);
  return first ? overrideKeyFor(slots, first) : String(dayOfWeek);
}

/** A row's time, length and crew only: what decides when, and by whom, a visit happens. */
export function dayTimingOverrides(overrides: RuleFormState['dayOverrides']): Record<string, DayOverride> {
  return Object.fromEntries(
    Object.entries(overrides).map(([key, { priceCents: _price, checklistTemplateId: _checklist, ...timing }]) => [
      key,
      timing,
    ]),
  );
}

/** Nothing on this row differs from the schedule, so it need not be kept. */
function overrideIsEmpty(override: DayOverride): boolean {
  return (
    override.startTimeLocal === '' &&
    override.assignedUserIds === null &&
    !override.durationMinutes &&
    override.priceCents == null &&
    !override.checklistTemplateId
  );
}

/** The rule the preview, the price, and the API all read. */
export function ruleToRecurrence(rule: RuleFormState): RecurrenceRule {
  const weekly = rule.frequency === 'weekly' || rule.frequency === 'biweekly';
  const nth = rule.frequency === 'monthly' && rule.monthlyMode === 'nth_weekday';
  const days = sortedUnique(rule.weekdays.length > 0 ? rule.weekdays : [rule.dayOfWeek]);
  const preserved = matchingPreservedSlots(rule);
  const sharedNths = nth ? sortedUnique(rule.nthWeeks.length > 0 ? rule.nthWeeks : [rule.nthWeek]) : null;
  const slots =
    rule.frequency === 'one_time' || (rule.frequency === 'monthly' && rule.monthlyMode === 'day_of_month')
      ? null
      : preserved
        ? preserved.map((slot) =>
            slotFromOverride(
              slot.dayOfWeek,
              sortedUnique(slot.nthWeeks ?? []),
              rule.dayOverrides[overrideKeyFor(preserved, slot)],
              rule.dayOverrides[weekdayKeyFor(preserved, slot.dayOfWeek)],
            ),
          )
        : days.map((dayOfWeek) => slotFromOverride(dayOfWeek, sharedNths, rule.dayOverrides[String(dayOfWeek)]));
  return {
    frequency: rule.frequency,
    dayOfWeek: weekly || nth ? (days[0] ?? null) : null,
    dayOfMonth: rule.frequency === 'monthly' && rule.monthlyMode === 'day_of_month' ? rule.dayOfMonth : null,
    nthWeek: nth ? (rule.nthWeeks[0] ?? rule.nthWeek) : null,
    nthWeeks: nth ? sortedUnique(rule.nthWeeks.length > 0 ? rule.nthWeeks : [rule.nthWeek]) : null,
    intervalWeeks: weekly ? rule.intervalWeeks : null,
    weekdays: weekly || nth ? days : undefined,
    daySlots: slots,
    startsOn: rule.startsOn,
    endsOn: rule.endsOn || null,
  };
}

/**
 * The first visit the rule actually books, for the availability grid to ghost
 * and check: the same visit POST /series and a series edit check first.
 *
 * `startsOn` is "first visit on or after", not the first visit: a Monday
 * schedule started on a Saturday first visits on the Monday after. Ghosting
 * the Saturday drew the visit on a day nothing is booked and warned about
 * whatever blocked that Saturday ("That time overlaps something else"), while
 * the server checks the real first visit and saved the schedule without
 * complaint. The server plans from today in the schedule's timezone
 * (plannedOccurrences), so a start date in the past (normal in the series
 * editor) is read from `today` instead. The time and length come from that
 * day's own slot through the same occurrenceWindow the server uses.
 *
 * Null until a full date is entered. When the rule yields no visit at all (an
 * end date before the first match), the typed date itself, so the grid still
 * shows where the office is looking.
 */
export function firstVisitSelection(
  rule: RuleFormState,
  today: string,
): { date: string; time: string; durationMinutes: number } | null {
  if (rule.startsOn.length !== 10) return null;
  const recurrence = ruleToRecurrence(rule);
  const from = today.length === 10 && today > rule.startsOn ? today : rule.startsOn;
  const date = previewOccurrenceDates(recurrence, from, 1)[0] ?? rule.startsOn;
  const window = occurrenceWindow(recurrence, date, rule.startTimeLocal, rule.durationMinutes);
  return { date, time: window.startTimeLocal, durationMinutes: window.durationMinutes };
}

export function ruleToApiFields(rule: RuleFormState): Record<string, unknown> {
  const recurrence = ruleToRecurrence(rule);
  return {
    frequency: recurrence.frequency,
    dayOfWeek: recurrence.dayOfWeek ?? null,
    dayOfMonth: recurrence.dayOfMonth ?? null,
    nthWeek: recurrence.nthWeek ?? null,
    nthWeeks: recurrence.nthWeeks ?? null,
    intervalWeeks: recurrence.intervalWeeks ?? null,
    // Price and checklist travel as explicit nulls when blank: the server keeps
    // a weekday's saved ones when a save leaves the fields out (older clients
    // do), so "Same as schedule" has to be said, not implied.
    daySlots: recurrence.daySlots?.map((slot) => ({ priceCents: null, checklistTemplateId: null, ...slot })) ?? null,
    startTimeLocal: rule.startTimeLocal,
    durationMinutes: rule.durationMinutes,
    startsOn: rule.startsOn,
    endsOn: rule.endsOn || null,
  };
}

/** '2026-08-11' → 2 (Tuesday); read at UTC noon so no zone can shift the day. */
function weekdayOf(isoDate: string): number {
  return new Date(`${isoDate}T12:00:00Z`).getUTCDay();
}

/** Which week of the month a date is: 1–4 from the start, 5 in a fifth week. */
function nthWeekOfDate(isoDate: string): number {
  return Math.floor((Number(isoDate.slice(8, 10)) - 1) / 7) + 1;
}

/**
 * The weeks a pick should leave selected.
 *
 * A grid click names one occurrence, so a form that says "the 1st Tuesday"
 * becomes "the 3rd Thursday" when the 3rd Thursday is what was clicked.
 * A pattern that already lists several weeks keeps them when the click lands
 * on one of those weeks; a click on a week it does not list replaces it,
 * because otherwise the date on the grid is not a visit.
 */
function nthWeeksForPick(
  current: RuleFormState,
  date: string,
  monthlyNthWeek?: number | null,
): { nthWeek: number; nthWeeks: number[] } {
  const picked = monthlyNthWeek ?? nthWeekOfDate(date);
  const currentNths = sortedUnique(current.nthWeeks.length > 0 ? current.nthWeeks : [current.nthWeek]);
  const keep = monthlyNthWeek == null && currentNths.length > 1 && currentNths.includes(picked);
  const next = keep ? currentNths : [picked];
  return { nthWeek: next[0] ?? picked, nthWeeks: next };
}

/**
 * A picked date and time folded into the rule, keeping every frequency's
 * recurrence fields in step: weekly rules follow the picked weekday, monthly
 * ones the picked day of month (or its weekday, in nth-weekday mode) — so the
 * schedule that saves is the one the picker showed. Shared by the booking
 * form and the series editor, which must not disagree about what a click
 * means.
 *
 * A pattern that already names several weekdays keeps them. The click sets
 * when the schedule starts, not which days it repeats on.
 */
export function ruleWithPick(
  current: RuleFormState,
  date: string,
  time: string,
  /**
   * The monthly anchor a quick pick was SCORED under. Quick picks weigh
   * monthly rules by weekday — "the second Tuesday", which every month has —
   * so taking one has to switch the form to that mode: storing the pick as
   * "the 8th" would be a different rule from the one whose clashes were
   * counted, and on the 29th–31st a rule that skips short months entirely.
   * Absent for a click on the grid, which says nothing about the anchor and
   * leaves the mode the office chose alone.
   */
  monthlyNthWeek?: number | null,
): RuleFormState {
  const takesNthWeekday =
    current.frequency === 'monthly' && (monthlyNthWeek != null || current.monthlyMode === 'nth_weekday');
  const pickedDay = weekdayOf(date);
  const keepWeekdays = current.weekdays.length > 1;
  const weekly = current.frequency === 'weekly' || current.frequency === 'biweekly';
  const next: RuleFormState = {
    ...current,
    startsOn: date,
    startTimeLocal: time,
    ...(weekly && !keepWeekdays ? { dayOfWeek: pickedDay, weekdays: [pickedDay] } : {}),
    ...(current.frequency === 'monthly' && !takesNthWeekday ? { dayOfMonth: Number(date.slice(8, 10)) } : {}),
    ...(takesNthWeekday
      ? {
          monthlyMode: 'nth_weekday' as const,
          ...(keepWeekdays ? {} : { dayOfWeek: pickedDay, weekdays: [pickedDay] }),
          ...nthWeeksForPick(current, date, monthlyNthWeek),
        }
      : {}),
  };
  // A pick that leaves a monthly split's weeks behind drops the split, and
  // its weekday prices and checklists move with it (`withoutSplit`).
  return matchingPreservedSlots(current) && !matchingPreservedSlots(next) ? withoutSplit(next) : next;
}

/**
 * Whether picking this date/time right now would leave the rule exactly as
 * it already is — not just startsOn/startTimeLocal, but every field a pick
 * touches (dayOfWeek, monthlyMode, dayOfMonth, nthWeek). Those fields have
 * their own inputs and can be edited after a chip is picked, so a plain
 * date/time comparison would keep a chip looking selected once its implied
 * weekday or monthly anchor no longer matches what's actually set.
 */
export function ruleMatchesPick(
  rule: RuleFormState,
  date: string,
  time: string,
  monthlyNthWeek?: number | null,
): boolean {
  const candidate = ruleWithPick(rule, date, time, monthlyNthWeek);
  return (Object.keys(rule) as (keyof RuleFormState)[]).every(
    (key) => JSON.stringify(rule[key]) === JSON.stringify(candidate[key]),
  );
}

interface RuleFieldsProps {
  rule: RuleFormState;
  onChange: (next: RuleFormState) => void;
  fieldErrors?: Record<string, string>;
  disabled?: boolean;
  lockFrequency?: boolean;
  lockDate?: boolean;
  /**
   * The schedule's currency. Given, each weekday row offers its own price
   * ("Same as schedule" when blank).
   */
  currencyCode?: string;
  /**
   * Checklist templates the schedule can use. Given, each weekday row offers
   * its own checklist ("Same as schedule" when none is picked).
   */
  checklistTemplates?: ReadonlyArray<{ id: string; name: string }>;
  /**
   * Told whether any weekday price box holds text that is not an amount. The
   * form keeps that weekday's saved price meanwhile (only an empty box clears
   * it), so the parent has to hold Save until the box is fixed or emptied.
   */
  onDayPriceInvalidChange?: (invalid: boolean) => void;
}

export function RuleFields({
  rule,
  onChange,
  fieldErrors = {},
  disabled,
  lockFrequency,
  lockDate,
  currencyCode,
  checklistTemplates,
  onDayPriceInvalidChange,
}: RuleFieldsProps) {
  const { t } = useTranslation();
  // Weekday price boxes holding unreadable text, by override key. The ref is
  // read in the same keystroke the box reports it, before state catches up.
  const [unreadablePrices, setUnreadablePrices] = useState<Record<string, boolean>>({});
  const unreadablePriceRef = useRef<Record<string, boolean>>({});
  const set = <K extends keyof RuleFormState>(key: K, value: RuleFormState[K]) => onChange({ ...rule, [key]: value });

  const showWeekday =
    rule.frequency === 'weekly' ||
    rule.frequency === 'biweekly' ||
    (rule.frequency === 'monthly' && rule.monthlyMode === 'nth_weekday');
  const showInterval = rule.frequency === 'weekly' || rule.frequency === 'biweekly';
  const days = sortedUnique(rule.weekdays.length > 0 ? rule.weekdays : [rule.dayOfWeek]);

  const preview = rule.startsOn.length === 10 ? previewOccurrenceDates(ruleToRecurrence(rule), rule.startsOn, 4) : [];

  const toggleWeekday = (day: number) => {
    const next = days.includes(day) ? days.filter((current) => current !== day) : [...days, day];
    const kept = next.length > 0 ? sortedUnique(next) : days;
    onChange(withoutSplit({ ...rule, weekdays: kept, dayOfWeek: kept[0] ?? rule.dayOfWeek }));
  };

  const toggleNth = (nth: number) => {
    const current = sortedUnique(rule.nthWeeks.length > 0 ? rule.nthWeeks : [rule.nthWeek]);
    const next = current.includes(nth) ? current.filter((value) => value !== nth) : [...current, nth];
    const kept = next.length > 0 ? sortedUnique(next) : current;
    onChange(withoutSplit({ ...rule, nthWeeks: kept, nthWeek: kept[0] ?? rule.nthWeek }));
  };

  const setFrequency = (frequency: SeriesFrequency) => onChange(withFrequency(rule, frequency));

  const patchOverride = (key: string, override: DayOverride | null) => {
    const dayOverrides = { ...rule.dayOverrides };
    if (override === null || overrideIsEmpty(override)) {
      delete dayOverrides[key];
    } else {
      dayOverrides[key] = override;
    }
    onChange({ ...rule, dayOverrides });
  };

  const preserved = matchingPreservedSlots(rule);
  const overrideEntries: Array<{ key: string; label: string; dayName: string; ownsWeekday: boolean }> = preserved
    ? preserved.map((slot) => {
        const dayName = t(`schedule.dayOfWeek.${slot.dayOfWeek}`);
        const weeks = (slot.nthWeeks ?? []).map((nth) => t(`schedule.nthWeek.${nth}`)).join(', ');
        const key = overrideKeyFor(preserved, slot);
        return {
          key,
          label: weeks ? `${dayName} (${weeks})` : dayName,
          dayName,
          ownsWeekday: key === weekdayKeyFor(preserved, slot.dayOfWeek),
        };
      })
    : days.length > 1
      ? days.map((day) => {
          const dayName = t(`schedule.dayOfWeek.${day}`);
          return { key: String(day), label: dayName, dayName, ownsWeekday: true };
        })
      : [];

  /**
   * A weekday's own price and checklist. On its first row when the schedule
   * visits several days, and on its own for a schedule with one day, so a
   * value saved while other days were selected can still be seen and cleared.
   */
  const dayExtras = (entry: { key: string; dayName: string }, override: DayOverride) =>
    currencyCode || checklistTemplates ? (
      <div className="mt-3 grid gap-x-4 sm:grid-cols-2">
        {currencyCode ? (
          <MoneyInput
            label={t('ruleFields.dayPrice', { day: entry.dayName })}
            hint={t('ruleFields.dayPriceHint')}
            placeholder={t('ruleFields.sameAsSchedule')}
            valueCents={override.priceCents ?? null}
            currencyCode={currencyCode}
            disabled={disabled}
            {...(unreadablePrices[entry.key] ? { error: t('ruleFields.dayPriceInvalid') } : {})}
            onInvalidChange={(invalid) => {
              unreadablePriceRef.current[entry.key] = invalid;
              setUnreadablePrices((current) =>
                Boolean(current[entry.key]) === invalid ? current : { ...current, [entry.key]: invalid },
              );
            }}
            onChangeCents={(priceCents) => {
              // Unreadable text is not a cleared price: keep the saved one.
              if (priceCents === null && unreadablePriceRef.current[entry.key]) return;
              patchOverride(entry.key, { ...override, priceCents });
            }}
          />
        ) : null}
        {checklistTemplates ? (
          <Select
            label={t('ruleFields.dayChecklist', { day: entry.dayName })}
            value={override.checklistTemplateId ?? ''}
            disabled={disabled}
            options={[
              { value: '', label: t('ruleFields.sameAsSchedule') },
              ...checklistTemplates.map((template) => ({ value: template.id, label: template.name })),
              // A template this list no longer offers (another
              // location's, or deleted) still shows what is saved.
              ...(override.checklistTemplateId &&
              !checklistTemplates.some((template) => template.id === override.checklistTemplateId)
                ? [{ value: override.checklistTemplateId, label: t('ruleFields.unavailableChecklist') }]
                : []),
            ]}
            onChange={(event) =>
              patchOverride(entry.key, { ...override, checklistTemplateId: event.target.value || null })
            }
          />
        ) : null}
      </div>
    ) : null;
  const soleDay = !preserved && days.length === 1 ? days[0] : undefined;
  const soleEntry =
    soleDay === undefined ? null : { key: String(soleDay), dayName: t(`schedule.dayOfWeek.${soleDay}`) };

  // The price boxes on screen. One that is gone took its text with it, and
  // comes back showing the price the form kept.
  const priceKeys =
    !showWeekday || !currencyCode
      ? []
      : overrideEntries.length > 1
        ? overrideEntries.filter((entry) => entry.ownsWeekday).map((entry) => entry.key)
        : soleEntry
          ? [soleEntry.key]
          : [];
  const priceKeysKey = priceKeys.join('|');
  useEffect(() => {
    const shown = new Set(priceKeysKey.split('|'));
    for (const key of Object.keys(unreadablePriceRef.current))
      if (!shown.has(key)) delete unreadablePriceRef.current[key];
    setUnreadablePrices((current) =>
      Object.keys(current).every((key) => shown.has(key))
        ? current
        : Object.fromEntries(Object.entries(current).filter(([key]) => shown.has(key))),
    );
  }, [priceKeysKey]);
  const anyPriceUnreadable = priceKeys.some((key) => unreadablePrices[key]);
  useEffect(() => {
    onDayPriceInvalidChange?.(anyPriceUnreadable);
  }, [anyPriceUnreadable, onDayPriceInvalidChange]);
  useEffect(() => () => onDayPriceInvalidChange?.(false), [onDayPriceInvalidChange]);

  return (
    <>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Select
          label={t('ruleFields.howOften')}
          value={rule.frequency}
          disabled={disabled || lockFrequency}
          error={fieldErrors.frequency}
          options={SERIES_FREQUENCIES.map((frequency) => ({
            value: frequency,
            label: t(`schedule.frequency.${frequency}`),
          }))}
          onChange={(event) => setFrequency(event.target.value as SeriesFrequency)}
        />

        {showInterval ? (
          <NumberInput
            label={t('ruleFields.everyNWeeks')}
            min={1}
            max={52}
            hint={t('ruleFields.everyNWeeksHint')}
            value={rule.intervalWeeks}
            disabled={disabled}
            error={fieldErrors.intervalWeeks}
            onValueChange={(weeks) => set('intervalWeeks', Math.min(52, Math.max(1, weeks ?? 1)))}
          />
        ) : null}

        {rule.frequency === 'monthly' ? (
          <Select
            label={t('ruleFields.repeatsOn')}
            value={rule.monthlyMode}
            disabled={disabled}
            options={[
              { value: 'day_of_month', label: t('ruleFields.dayOfMonthOption') },
              { value: 'nth_weekday', label: t('ruleFields.nthWeekdayOption') },
            ]}
            onChange={(event) => {
              const monthlyMode = event.target.value as RuleFormState['monthlyMode'];
              onChange(
                monthlyMode === 'nth_weekday' ? { ...rule, monthlyMode } : withoutSplit({ ...rule, monthlyMode }),
              );
            }}
          />
        ) : null}

        {rule.frequency === 'monthly' && rule.monthlyMode === 'day_of_month' ? (
          <NumberInput
            label={t('ruleFields.dayOfMonthLabel')}
            min={1}
            max={31}
            hint={t('ruleFields.dayOfMonthHint')}
            value={rule.dayOfMonth}
            disabled={disabled}
            error={fieldErrors.dayOfMonth}
            onValueChange={(day) => set('dayOfMonth', day ?? 0)}
          />
        ) : null}

        {rule.frequency === 'monthly' && rule.monthlyMode === 'nth_weekday' ? (
          <fieldset className="mb-4">
            <legend className="field-label">{t('ruleFields.whichWeeks')}</legend>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((nth) => (
                <Checkbox
                  key={nth}
                  label={t(`schedule.nthWeek.${nth}`)}
                  checked={(rule.nthWeeks.length > 0 ? rule.nthWeeks : [rule.nthWeek]).includes(nth)}
                  disabled={disabled}
                  onChange={() => toggleNth(nth)}
                />
              ))}
            </div>
          </fieldset>
        ) : null}

        {showWeekday ? (
          <fieldset className="mb-4 sm:col-span-2">
            <legend className="field-label">{t('ruleFields.weekdaysLabel')}</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {DAYS_OF_WEEK.map((day) => (
                <Checkbox
                  key={day.value}
                  label={t(`schedule.dayOfWeek.${day.value}`)}
                  checked={days.includes(day.value)}
                  disabled={disabled}
                  onChange={() => toggleWeekday(day.value)}
                />
              ))}
            </div>
            {fieldErrors.dayOfWeek || fieldErrors.daySlots ? (
              <p className="field-error">{fieldErrors.dayOfWeek ?? fieldErrors.daySlots}</p>
            ) : null}
          </fieldset>
        ) : null}

        <TextInput
          label={t('ruleFields.startTime')}
          type="time"
          step={300}
          required
          value={rule.startTimeLocal}
          disabled={disabled}
          error={fieldErrors.startTimeLocal}
          onChange={(event) => set('startTimeLocal', event.target.value)}
        />

        <NumberInput
          label={t('ruleFields.estimatedDuration')}
          min={15}
          max={1440}
          step={5}
          required
          value={rule.durationMinutes}
          disabled={disabled}
          error={fieldErrors.durationMinutes}
          onValueChange={(minutes) => set('durationMinutes', minutes ?? 0)}
        />

        <TextInput
          label={rule.frequency === 'one_time' ? t('ruleFields.visitDate') : t('ruleFields.firstVisitOnOrAfter')}
          type="date"
          required
          disabled={disabled || lockDate}
          value={rule.startsOn}
          error={fieldErrors.startsOn}
          onChange={(event) => set('startsOn', event.target.value)}
        />

        {rule.frequency !== 'one_time' ? (
          <Field label={t('ruleFields.endsOn')} hint={t('ruleFields.endsOnHint')} error={fieldErrors.endsOn}>
            {({ id, describedBy, invalid }) => (
              <div className="flex gap-2">
                <input
                  id={id}
                  type="date"
                  value={rule.endsOn}
                  disabled={disabled}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  className="field-control min-w-0 flex-1"
                  onChange={(event) => set('endsOn', event.target.value)}
                />
                {rule.endsOn ? (
                  <Button type="button" variant="ghost" disabled={disabled} onClick={() => set('endsOn', '')}>
                    {t('ruleFields.clearEndsOn')}
                  </Button>
                ) : null}
              </div>
            )}
          </Field>
        ) : null}
      </div>

      {showWeekday && overrideEntries.length > 1 ? (
        <div className="mb-4 space-y-4">
          {overrideEntries.map((entry) => {
            const override = rule.dayOverrides[entry.key] ?? { startTimeLocal: '', assignedUserIds: null };
            return (
              <div key={entry.key} className="rounded-lg border border-[color:var(--color-line)] p-3">
                <TextInput
                  label={t('ruleFields.dayStartsAt', { day: entry.label })}
                  hint={t('ruleFields.dayStartsHint', { time: formatLocalTime(rule.startTimeLocal) })}
                  type="time"
                  step={300}
                  value={override.startTimeLocal}
                  disabled={disabled}
                  onChange={(event) => patchOverride(entry.key, { ...override, startTimeLocal: event.target.value })}
                />
                <Checkbox
                  label={t('ruleFields.differentCrew', { day: entry.label })}
                  checked={override.assignedUserIds !== null}
                  disabled={disabled}
                  onChange={(event) =>
                    patchOverride(entry.key, {
                      ...override,
                      assignedUserIds: event.target.checked ? [] : null,
                    })
                  }
                />
                {override.assignedUserIds !== null ? (
                  <AssigneePicker
                    compact
                    label={t('ruleFields.dayCrewLabel', { day: entry.label })}
                    value={override.assignedUserIds}
                    disabled={disabled}
                    onChange={(assignedUserIds) => patchOverride(entry.key, { ...override, assignedUserIds })}
                  />
                ) : null}
                {entry.ownsWeekday ? dayExtras(entry, override) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {showWeekday && soleEntry && (currencyCode || checklistTemplates) ? (
        <div className="mb-4 rounded-lg border border-[color:var(--color-line)] p-3">
          {dayExtras(soleEntry, rule.dayOverrides[soleEntry.key] ?? { startTimeLocal: '', assignedUserIds: null })}
        </div>
      ) : null}

      {preview.length > 0 && rule.frequency !== 'one_time' ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">
          {t('ruleFields.nextVisits', { dates: preview.map(formatShortDate).join(' · ') })}
        </p>
      ) : null}
    </>
  );
}

/** The editor's form for a series the API already stored. */
export function ruleStateFromSeries(series: {
  frequency: SeriesFrequency;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  nthWeek: number | null;
  intervalWeeks: number | null;
  daySlots: DaySlot[] | null;
  startTimeLocal: string;
  durationMinutes: number;
  startsOn: string;
  endsOn: string | null;
}): RuleFormState {
  const slots = series.daySlots ?? [];
  const weekdays = [...new Set(slots.length > 0 ? slots.map((slot) => slot.dayOfWeek) : [series.dayOfWeek ?? 1])].sort(
    (left, right) => left - right,
  );
  const nthWeeks = [...new Set(slots.flatMap((slot) => slot.nthWeeks ?? []))].sort((left, right) => left - right);
  const seen = new Set<number>();
  const repeated = slots.some((slot) => {
    const again = seen.has(slot.dayOfWeek);
    seen.add(slot.dayOfWeek);
    return again;
  });
  const dayOverrides: RuleFormState['dayOverrides'] = {};
  for (const slot of slots) {
    const key = overrideKeyFor(slots, slot);
    // Price and checklist belong to the weekday, so only its first row holds them.
    const ownsWeekday = key === weekdayKeyFor(slots, slot.dayOfWeek);
    const priceCents = ownsWeekday ? (slot.priceCents ?? null) : null;
    const checklistTemplateId = ownsWeekday ? (slot.checklistTemplateId ?? null) : null;
    if (
      slot.startTimeLocal ||
      slot.durationMinutes ||
      slot.assignedUserIds != null ||
      priceCents !== null ||
      checklistTemplateId
    ) {
      dayOverrides[key] = {
        startTimeLocal: slot.startTimeLocal ?? '',
        assignedUserIds: slot.assignedUserIds ?? null,
        ...(slot.durationMinutes ? { durationMinutes: slot.durationMinutes } : {}),
        ...(priceCents !== null ? { priceCents } : {}),
        ...(checklistTemplateId ? { checklistTemplateId } : {}),
      };
    }
  }
  return {
    frequency: series.frequency,
    dayOfWeek: series.dayOfWeek ?? weekdays[0] ?? 1,
    weekdays,
    intervalWeeks: series.intervalWeeks ?? (series.frequency === 'biweekly' ? 2 : 1),
    monthlyMode: series.nthWeek || nthWeeks.length > 0 ? 'nth_weekday' : 'day_of_month',
    dayOfMonth: series.dayOfMonth ?? 1,
    nthWeek: series.nthWeek ?? nthWeeks[0] ?? 1,
    nthWeeks: nthWeeks.length > 0 ? nthWeeks : [series.nthWeek ?? 1],
    dayOverrides,
    preservedSlots: repeated ? slots : null,
    startTimeLocal: series.startTimeLocal,
    durationMinutes: series.durationMinutes,
    startsOn: series.startsOn,
    endsOn: series.endsOn ?? '',
  };
}

export function defaultRuleState(today: string): RuleFormState {
  return {
    frequency: 'weekly',
    dayOfWeek: 1,
    weekdays: [1],
    intervalWeeks: 1,
    monthlyMode: 'day_of_month',
    dayOfMonth: 1,
    nthWeek: 1,
    nthWeeks: [1],
    dayOverrides: {},
    startTimeLocal: '09:00',
    durationMinutes: 120,
    startsOn: today,
    endsOn: '',
  };
}
